import { Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import { TimelineDateRange } from '../calendarTypes';
import { getDayRangePosition, toCivilDate } from '../timelineCalendarDates';

export interface TimelineCalendarDayExtraProps {
  eventDays?: Set<string>;
  range?: TimelineDateRange;
}

export type TimelineCalendarDayProps = PickersDayProps<Date> &
  TimelineCalendarDayExtraProps;

export const EVENT_DAY_TOOLTIP = 'Há eventos neste dia';
export const CALENDAR_DAY_SIZE = 32;
/** Célula + margem horizontal padrão do PickersDay (2px de cada lado). */
export const CALENDAR_DAY_PITCH = CALENDAR_DAY_SIZE + 4;

export default function TimelineCalendarDay({
  eventDays,
  range = { dateStart: '', dateEnd: '' },
  sx,
  ...dayProps
}: TimelineCalendarDayProps) {
  const { day, outsideCurrentMonth, disabled } = dayProps;
  const civilDay = toCivilDate(day);
  const hasEvents =
    !outsideCurrentMonth && !disabled && Boolean(eventDays?.has(civilDay));
  const { isRangeStart, isRangeEnd, isInRange } = getDayRangePosition(
    civilDay,
    range,
  );
  const isSelected = !outsideCurrentMonth && isInRange;
  const isEndpoint = isRangeStart || isRangeEnd;
  const labelParts = [format(day, "d 'de' MMMM 'de' yyyy", { locale: ptBR })];

  if (hasEvents) labelParts.push('há eventos');
  if (isSelected) {
    labelParts.push(
      isRangeStart && isRangeEnd
        ? 'dia selecionado'
        : isRangeStart
          ? 'início do período selecionado'
          : isRangeEnd
            ? 'fim do período selecionado'
            : 'no período selecionado',
    );
  }

  const pickersDay = (
    <PickersDay
      {...dayProps}
      selected={isSelected}
      aria-selected={isSelected}
      aria-label={labelParts.join(', ')}
      data-has-events={hasEvents ? 'true' : undefined}
      sx={[
        (theme) => {
          const rangeBand = alpha(theme.palette.text.primary, 0.06);

          return {
            position: 'relative',
            width: CALENDAR_DAY_SIZE,
            height: CALENDAR_DAY_SIZE,
            fontSize: theme.typography.caption.fontSize,
            ...(hasEvents && {
              fontWeight: 700,
              color: theme.palette.text.primary,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 3,
                left: '50%',
                width: 4,
                height: 4,
                borderRadius: 2,
                transform: 'translateX(-50%)',
                backgroundColor: theme.palette.primary.main,
              },
            }),
            '&.Mui-selected': isEndpoint
              ? {
                  color: theme.palette.common.white,
                  backgroundColor: theme.palette.grey[800],
                  '&:hover, &:focus': {
                    backgroundColor: theme.palette.grey[900],
                  },
                }
              : {
                  color: theme.palette.text.primary,
                  backgroundColor: rangeBand,
                  // Sem margem e com a largura do passo, a faixa fica contínua.
                  width: CALENDAR_DAY_PITCH,
                  mx: 0,
                  borderRadius: 0,
                  '&:hover, &:focus': {
                    backgroundColor: alpha(theme.palette.text.primary, 0.12),
                  },
                },
          };
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    />
  );

  if (!hasEvents) return pickersDay;

  return (
    <Tooltip title={EVENT_DAY_TOOLTIP} describeChild>
      <span style={{ display: 'inline-flex' }}>{pickersDay}</span>
    </Tooltip>
  );
}
